import React, { useState, useEffect, useCallback } from 'react';
import { ListGroup, Spinner, Alert } from 'react-bootstrap';
import { FaFileAlt, FaTrash, FaUpload, FaDownload } from 'react-icons/fa';
import { apiFetch, mediaUrl } from '../api';
import { useAuth } from '../auth/AuthContext';
import { canUploadFiles } from '../auth/roles';
import ConfirmDeleteModal from './ConfirmDeleteModal';

function formatSize(bytes) {
  if (bytes === null || bytes === undefined) return '';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(i === 0 || value >= 10 ? 0 : 1)} ${units[i]}`;
}

function ContractFiles({ contractId, compact = false, showUpload = true }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { user } = useAuth();
  const canUpload = canUploadFiles(user?.role);

  const fetchFiles = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    apiFetch(`/api/contract-files/?contract=${contractId}`)
      .then((data) => {
        setFiles(data.results || data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Не удалось загрузить список файлов');
        setLoading(false);
      });
  }, [contractId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleUpload = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    setUploading(true);
    setError(null);

    const uploadOne = (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('contract', contractId);
      return apiFetch('/api/contract-files/', { method: 'POST', body: formData });
    };

    Promise.all(selected.map(uploadOne))
      .then(() => fetchFiles(true))
      .catch((err) => {
        console.error(err);
        setError(err.message || 'Не удалось загрузить файлы');
      })
      .finally(() => {
        setUploading(false);
        e.target.value = '';
      });
  };

  const handleActionClick = (e) => e.stopPropagation();

  return (
    <div className={`contract-files ${compact ? 'contract-files-compact' : ''}`} onClick={handleActionClick}>
      {showUpload && canUpload && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <span className="app-text-muted small">Файлы договора</span>
          <div>
            <label
              htmlFor={`contract-file-upload-${contractId}`}
              className="app-btn app-btn-primary app-btn-sm mb-0"
              style={{ cursor: 'pointer' }}
              onClick={handleActionClick}
            >
              <FaUpload /> {uploading ? 'Загрузка...' : 'Добавить'}
            </label>
            <input
              id={`contract-file-upload-${contractId}`}
              type="file"
              multiple
              onChange={handleUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-1">
          <Spinner animation="border" size="sm" />
        </div>
      ) : files.length === 0 ? (
        <div className="app-text-muted small">Файлов пока нет</div>
      ) : (
        <ListGroup variant="flush" className={compact ? 'd-inline-flex flex-wrap gap-1' : ''}>
          {files.map((f) => (
            <ListGroup.Item
              key={f.id}
              className={`d-flex justify-content-between align-items-center flex-wrap gap-1 px-0 py-1 ${compact ? 'border-0 bg-transparent' : ''}`}
              style={{ background: 'transparent', borderColor: 'var(--surface-border)' }}
              onClick={handleActionClick}
            >
              <div className="d-flex align-items-center gap-1" style={{ minWidth: 0 }}>
                <FaFileAlt className="app-text-muted" style={{ fontSize: '12px' }} />
                <div style={{ minWidth: 0 }}>
                  <div className="text-truncate" style={{ maxWidth: 220 }}>
                    {f.original_name || (f.file || '').split('/').pop()}
                  </div>
                  {!compact && <div className="app-text-muted small">{formatSize(f.size)}</div>}
                </div>
              </div>
              <div className="d-flex gap-1">
                <a
                  href={mediaUrl(f.file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-btn app-btn-sm"
                  onClick={handleActionClick}
                >
                  <FaDownload />
                </a>
                {canUpload && (
                  <button
                    type="button"
                    className="app-btn app-btn-sm app-btn-danger-outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(f);
                    }}
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      <ConfirmDeleteModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await apiFetch(`/api/contract-files/${deleteTarget.id}/`, { method: 'DELETE' });
          setDeleteTarget(null);
          fetchFiles(true);
        }}
        title="Удалить файл?"
        message="Файл будет удалён без возможности восстановления."
        itemName={deleteTarget?.original_name || deleteTarget?.file?.split('/').pop()}
      />
    </div>
  );
}

export default ContractFiles;